/**
 * AI Module - Fact Pack Schema
 *
 * Zod schema for validating the Fact Pack sent to the LLM.
 * The Fact Pack is the single source of truth containing:
 * - User inputs
 * - Computed engine outputs
 * - Applicable rules
 * - Confidence metadata
 *
 * @version 2.1 - Added returnStatus, impactLevel per conversation feedback
 */

import { z } from 'zod';

// ============================================
// Reusable Sub-Schemas
// ============================================

export const confidenceLevelSchema = z.enum(['high', 'medium', 'low']);

export const countrySchema = z.enum(['USA', 'UAE', 'UK', 'Canada', 'Singapore']);

export const timingSchema = z.enum([
  'immediate',
  'before_return',
  'during_rnor',
  'post_rnor',
  'ongoing',
]);

export const impactSchema = z.enum(['critical', 'high', 'medium']);

export const actionCategorySchema = z.enum([
  'tax_optimization',
  'asset_rebalancing',
  'compliance',
  'account_setup',
  'insurance',
  'estate_planning',
  'timing',
]);

// ============================================
// Section 1: User Profile
// ============================================

export const profileSchema = z.object({
  firstName: z.string().optional(), // User's first name for personalization
  age: z.number().int().min(18).max(100),
  retirementAge: z.number().int().min(30).max(100),
  lifeExpectancy: z.number().int().min(50).max(120),
  currentCountry: countrySchema,
  targetCountry: z.literal('India'),
  targetReturnYear: z.number().int().min(2024).max(2050),
  targetReturnMonth: z.number().int().min(1).max(12).optional(),
  // v2.1 fix: Support already-returned users
  returnStatus: z.enum(['planning', 'returned']).default('planning'),
  maritalStatus: z.enum(['single', 'married']),
  spouseAge: z.number().int().min(18).max(100).optional(),
  hasChildren: z.boolean(),
  numberOfChildren: z.number().int().min(0).max(10).optional(),
  childrenAges: z.array(z.number().int().min(0).max(30)).optional(),
});

// ============================================
// Section 2: FIRE Snapshot
// ============================================

export const fireSnapshotSchema = z.object({
  fireNumber: z.number().min(0),
  fireNumberUSD: z.number().min(0),
  currentNetWorth: z.number().min(0),
  currentNetWorthUSD: z.number().min(0),
  progressPercent: z.number().min(0).max(500), // Can exceed 100% if over-funded
  yearsToFIRE: z.number().min(0),
  monthsToFIRE: z.number().min(0),
  fireStatus: z.enum(['achieved', 'on_track', 'at_risk', 'behind']),
  fireType: z.enum(['lean', 'regular', 'fat']),
  monthlyExpensesAtFIRE_INR: z.number().min(0),
  annualExpensesAtFIRE_INR: z.number().min(0),
  safeWithdrawalRate: z.number().min(0.01).max(0.10),
  confidence: confidenceLevelSchema,
  confidenceFactors: z.array(z.string()),
});

// ============================================
// Section 3: Assets by Geography
// ============================================

export const residentCountryAssetsSchema = z.object({
  total: z.number().min(0),
  currency: z.string(),
  breakdown: z.object({
    retirement: z.number().min(0),
    taxableInvestments: z.number().min(0),
    rsusOptions: z.number().min(0),
    liquid: z.number().min(0),
    realEstate: z.number().min(0),
  }),
});

export const indiaAssetsSchema = z.object({
  total: z.number().min(0),
  currency: z.literal('INR'),
  breakdown: z.object({
    nps: z.number().min(0),
    epfPpf: z.number().min(0),
    mutualFunds: z.number().min(0),
    stocks: z.number().min(0),
    realEstate: z.number().min(0),
    nreNro: z.number().min(0),
    fd: z.number().min(0),
  }),
});

export const assetsSchema = z.object({
  residentCountry: residentCountryAssetsSchema,
  india: indiaAssetsSchema,
});

// ============================================
// Section 4: Tax Strategy
// ============================================

// v2.1 fix: RNOR applies to all per India IT Act, but impact varies by country
export const rnorInfoSchema = z.object({
  eligible: z.boolean(),
  eligibilityReason: z.string(),
  rnorStartDate: z.string(),
  rnorEndDate: z.string(),
  rnorYears: z.number().int().min(0).max(5),
  // v2.1 fix: UAE has "low" impact since no foreign income tax to optimize
  impactLevel: z.enum(['high', 'medium', 'low', 'none']),
});

export const residentialStatusTimelineItemSchema = z.object({
  fiscalYear: z.string(), // "FY 2028-29"
  status: z.enum(['NRI', 'RNOR', 'ROR']),
  foreignIncomeStatus: z.enum(['not_taxable', 'taxable']),
});

export const liquidationPlanItemSchema = z.object({
  asset: z.string(),
  recommendedAction: z.string(),
  recommendedTiming: z.string(),
  taxImplication: z.string(),
  estimatedTaxSavings_INR: z.number().min(0),
  requiresCAReview: z.boolean(),
  caReviewReason: z.string().optional(),
  confidence: confidenceLevelSchema,
  sourceRule: z.string(),
});

export const dtaaSchema = z.object({
  applicable: z.boolean(),
  treatyCountry: z.string(),
  keyBenefits: z.array(z.string()),
  relevantArticles: z.array(z.string()),
});

export const usSpecificSchema = z.object({
  exitTaxApplicable: z.boolean(),
  exitTaxReason: z.string().optional(),
  fbarRequired: z.boolean(),
  fatcaRequired: z.boolean(),
  estimatedFinalUSReturn: z.string(),
});

export const taxStrategySchema = z.object({
  rnpioBased: rnorInfoSchema,
  residentialStatusTimeline: z.array(residentialStatusTimelineItemSchema),
  liquidationPlan: z.array(liquidationPlanItemSchema),
  dtaa: dtaaSchema,
  usSpecific: usSpecificSchema.optional(),
  estimatedTotalTaxSavings_INR: z.number().min(0),
  savingsConfidence: confidenceLevelSchema,
});

// ============================================
// Section 5: Work Scenario
// ============================================

export const alternativeScenarioSchema = z.object({
  name: z.string(),
  returnDate: z.string(),
  fireImpact: z.string(),
  taxImpact: z.string(),
});

export const workScenarioSchema = z.object({
  recommendedPath: z.enum([
    'return_as_planned',
    'delay_return',
    'accelerate_return',
  ]),
  currentPlan: z.object({
    returnYear: z.number().int(),
    returnMonth: z.number().int().min(1).max(12).optional(),
  }),
  optimalPlan: z
    .object({
      returnYear: z.number().int(),
      returnMonth: z.number().int().min(1).max(12),
      rationale: z.string(),
      additionalCorpus_INR: z.number(),
      additionalTaxSavings_INR: z.number(),
    })
    .optional(),
  alternativeScenarios: z.array(alternativeScenarioSchema),
});

// ============================================
// Section 6: Priority Actions (Enhanced with calculated deadlines)
// ============================================

export const urgencyLevelSchema = z.enum(['critical', 'high', 'medium', 'low']);

export const priorityActionSchema = z.object({
  id: z.string(),
  category: actionCategorySchema,
  title: z.string().max(200),
  description: z.string().max(500),
  timing: timingSchema,
  // Legacy deadline field (human-readable string)
  deadline: z.string().optional(),
  // Enhanced deadline fields for calculated countdowns
  deadlineDate: z.string().optional(), // ISO date string (e.g., "2025-04-15")
  deadlineFormatted: z.string().optional(), // Human-readable (e.g., "April 15, 2025")
  daysRemaining: z.number().optional(), // Countdown (e.g., 83)
  urgency: urgencyLevelSchema.optional(), // Calculated from daysRemaining
  // Progress tracking
  completed: z.boolean().default(false),
  // Original fields
  impact: impactSchema,
  estimatedBenefit: z.string().optional(),
  requiresCAReview: z.boolean(),
  caReviewReason: z.string().optional(),
  sourceRule: z.string().optional(),
});

// ============================================
// Section 7: Constraints
// ============================================

export const constraintsSchema = z.object({
  targetCity: z.string(),
  targetCityTier: z.union([z.literal(1), z.literal(2), z.literal(3)]),
  monthlyCostEstimate_INR: z.number().min(0),
  housingPreference: z.enum(['rent', 'buy', 'undecided']),
  healthcareNeeds: z.enum(['standard', 'elevated', 'high']),
  childEducationYears: z.number().int().min(0).max(25).optional(),
});

// ============================================
// Section 8: Applicable Rules
// ============================================

export const rnorRulesSchema = z.object({
  summary: z.string(),
  eligibilityCriteria: z.array(z.string()),
  taxBenefits: z.array(z.string()),
  sourceUrl: z.string().optional(),
});

export const dtaaArticleSchema = z.object({
  article: z.string(),
  summary: z.string(),
});

export const dtaaRulesSchema = z.object({
  treatyName: z.string(),
  keyArticles: z.array(dtaaArticleSchema),
  sourceUrl: z.string().optional(),
});

export const countrySpecificRuleSchema = z.object({
  country: z.string(),
  ruleName: z.string(),
  summary: z.string(),
  applicability: z.string(),
  sourceUrl: z.string().optional(),
});

export const applicableRulesSchema = z.object({
  rnorRules: rnorRulesSchema,
  dtaaRules: dtaaRulesSchema.optional(),
  countrySpecificRules: z.array(countrySpecificRuleSchema),
});

// ============================================
// Section 9: Data Quality
// ============================================

export const missingFieldSchema = z.object({
  field: z.string(),
  impact: z.enum(['blocks_calculation', 'reduces_accuracy', 'nice_to_have']),
  assumption: z.string().optional(),
});

export const assumptionSchema = z.object({
  category: z.string(),
  assumption: z.string(),
  impact: z.string(),
});

export const dataQualitySchema = z.object({
  missingFields: z.array(missingFieldSchema),
  assumptions: z.array(assumptionSchema),
  overallConfidence: confidenceLevelSchema,
  confidenceExplanation: z.string(),
});

// ============================================
// Section 10: Pre-computed Journey Data
// ============================================

// Tax savings item (from Rule Engine)
export const preComputedTaxSavingsItemSchema = z.object({
  id: z.string(),
  category: z.string(),
  description: z.string(),
  amountINR: z.number(),
  amountFormatted: z.string(),
  timing: z.enum(['before_move', 'during_rnor', 'post_rnor']),
  timingDescription: z.string(),
  howToCapture: z.array(z.string()),
  confidence: z.enum(['high', 'medium', 'estimated']),
  requiresCAReview: z.boolean(),
});

// Tax savings breakdown
export const preComputedTaxSavingsSchema = z.object({
  total: z.number(),
  totalFormatted: z.string(),
  items: z.array(preComputedTaxSavingsItemSchema),
  assumptions: z.array(z.string()),
  disclaimer: z.string(),
});

// Social Security eligibility (USA only)
export const preComputedSSEligibilitySchema = z.object({
  eligible: z.boolean(),
  creditsEarned: z.number(),
  creditsNeeded: z.number(),
  yearsToQualify: z.number(),
  estimatedMonthlyBenefit: z.number(),
  lifetimeValue: z.number(),
  lifetimeValueFormatted: z.string().optional(), // "$432,000" - optional for backward compat
  lifetimeValueINR: z.number(),
  recommendation: z.enum(['eligible', 'stay_longer', 'return_later', 'too_far']).nullable(),
  recommendationText: z.string().optional(), // Made optional for edge cases
  isHighValueOpportunity: z.boolean(),
}).nullable();

// Scenario metrics
export const preComputedScenarioMetricsSchema = z.object({
  rnorYears: z.number(),
  rnorEligible: z.boolean(),
  taxSavingsINR: z.number(),
  taxSavingsFormatted: z.string(),
  additionalCorpusINR: z.number(),
  additionalCorpusFormatted: z.string(),
  ssEligibleAfter: z.boolean(),
  ssYearsNeeded: z.number(),
});

// Scenario
export const preComputedScenarioSchema = z.object({
  id: z.string(),
  name: z.string(),
  type: z.enum(['conservative', 'balanced', 'aggressive']),
  returnYear: z.number(),
  returnMonth: z.number(),
  returnDate: z.string(),
  returnFY: z.string(),
  isRecommended: z.boolean(),
  isCurrentPlan: z.boolean(),
  metrics: preComputedScenarioMetricsSchema,
  pros: z.array(z.string()),
  cons: z.array(z.string()),
  summary: z.string(),
});

// Scenario comparison
export const preComputedScenarioComparisonSchema = z.object({
  scenarios: z.array(preComputedScenarioSchema),
  recommendedScenarioId: z.string(),
  recommendationRationale: z.string(),
  keyTradeoff: z.string(),
});

// Liquidation item
export const preComputedLiquidationItemSchema = z.object({
  id: z.string(),
  sequence: z.number(),
  asset: z.string(),
  action: z.string(),
  timing: z.enum(['before_move', 'during_rnor', 'post_rnor']),
  timingDescription: z.string(),
  reason: z.string(),
  estimatedValueUSD: z.number(),
  estimatedValueINR: z.number(),
  taxImplication: z.string(),
  requiresCAReview: z.boolean(),
});

// Milestone
export const preComputedMilestoneSchema = z.object({
  id: z.string(),
  date: z.string(),
  title: z.string(),
  description: z.string(),
  category: z.enum(['preparation', 'transition', 'rnor', 'post_rnor', 'compliance']),
  isDeadline: z.boolean(),
  daysFromNow: z.number(),
  urgency: z.enum(['critical', 'high', 'medium', 'low']),
});

// Risk flag
export const preComputedRiskFlagSchema = z.object({
  id: z.string(),
  type: z.enum(['concentration', 'timing', 'currency', 'compliance', 'opportunity']),
  severity: z.enum(['high', 'medium', 'low']),
  title: z.string(),
  description: z.string(),
  recommendation: z.string(),
  relatedAsset: z.string().optional(),
  amountAtRisk: z.number().optional(),
});

// Hero stats
export const preComputedHeroStatsSchema = z.object({
  totalTaxSavingsINR: z.number(),
  totalTaxSavingsFormatted: z.string(),
  rnorYears: z.number(),
  rnorEligible: z.boolean(),
  ssEligible: z.boolean(),
  ssLifetimeValueINR: z.number().optional(),
  recommendedScenario: z.string(),
  daysUntilMove: z.number(),
});

// Key dates - All specific dates for LLM to use
export const preComputedDatesSchema = z.object({
  currentDate: z.string(),
  currentDateFormatted: z.string(),
  targetMoveDate: z.string(),
  targetMoveDateFormatted: z.string(),
  sixMonthsBeforeMove: z.string(),
  sixMonthsBeforeMoveFormatted: z.string(),
  threeMonthsBeforeMove: z.string(),
  threeMonthsBeforeMoveFormatted: z.string(),
  rnorStartDate: z.string().nullable(),
  rnorStartDateFormatted: z.string().nullable(),
  rnorEndDate: z.string().nullable(),
  rnorEndDateFormatted: z.string().nullable(),
  daysUntilMove: z.number(),
  monthsUntilMove: z.number(),
  fiscalYearOfReturn: z.string(),
});

// Insight seeds for LLM to reference
export const preComputedInsightSeedSchema = z.object({
  type: z.enum(['opportunity', 'warning', 'tip']),
  title: z.string(),
  keyData: z.string(),
  relatedAsset: z.string().optional(),
  amountFormatted: z.string().optional(),
  urgency: z.enum(['immediate', 'before_return', 'during_rnor', 'ongoing']).optional(),
});

// Deterministic confidence score breakdown
export const preComputedConfidenceScoreSchema = z.object({
  overall: z.number().int().min(0).max(100),
  level: z.enum(['high', 'medium', 'low']),
  components: z.object({
    rnorCertainty: z.number().int().min(0).max(100),
    assetDataCompleteness: z.number().int().min(0).max(100),
    taxCalculationAccuracy: z.number().int().min(0).max(100),
    projectionReliability: z.number().int().min(0).max(100),
  }),
  explanation: z.string(),
  limitations: z.array(z.string()),
  improvementSuggestions: z.array(z.string()),
});

// Main pre-computed journey data section
export const preComputedJourneyDataSchema = z.object({
  computedAt: z.string(),
  dataVersion: z.string(),
  dates: preComputedDatesSchema,
  taxSavings: preComputedTaxSavingsSchema,
  socialSecurity: preComputedSSEligibilitySchema,
  scenarios: preComputedScenarioComparisonSchema,
  liquidationSequence: z.array(preComputedLiquidationItemSchema),
  milestones: z.array(preComputedMilestoneSchema),
  riskFlags: z.array(preComputedRiskFlagSchema),
  insightSeeds: z.array(preComputedInsightSeedSchema),
  heroStats: preComputedHeroStatsSchema,
  confidenceScore: preComputedConfidenceScoreSchema,
});

// ============================================
// Main Fact Pack Schema
// ============================================

export const factPackSchema = z.object({
  // Meta
  generatedAt: z.string().datetime(),
  engineVersion: z.string(),

  // Sections
  profile: profileSchema,
  fireSnapshot: fireSnapshotSchema,
  assets: assetsSchema,
  taxStrategy: taxStrategySchema,
  workScenario: workScenarioSchema,
  priorityActions: z.array(priorityActionSchema),
  constraints: constraintsSchema,
  applicableRules: applicableRulesSchema,
  dataQuality: dataQualitySchema,

  // Pre-computed journey data (Rule Engine output)
  // LLM must use these exact values - no recalculation allowed
  preComputedJourneyData: preComputedJourneyDataSchema.optional(),
});

// ============================================
// Type Exports
// ============================================

export type FactPack = z.infer<typeof factPackSchema>;
export type Profile = z.infer<typeof profileSchema>;
export type FireSnapshot = z.infer<typeof fireSnapshotSchema>;
export type Assets = z.infer<typeof assetsSchema>;
export type TaxStrategy = z.infer<typeof taxStrategySchema>;
export type RnorInfo = z.infer<typeof rnorInfoSchema>;
export type WorkScenario = z.infer<typeof workScenarioSchema>;
export type PriorityAction = z.infer<typeof priorityActionSchema>;
export type Constraints = z.infer<typeof constraintsSchema>;
export type ApplicableRules = z.infer<typeof applicableRulesSchema>;
export type DataQuality = z.infer<typeof dataQualitySchema>;
export type LiquidationPlanItem = z.infer<typeof liquidationPlanItemSchema>;
export type ConfidenceLevel = z.infer<typeof confidenceLevelSchema>;
export type MissingField = z.infer<typeof missingFieldSchema>;
export type Assumption = z.infer<typeof assumptionSchema>;

// Pre-computed journey data types
export type PreComputedJourneyData = z.infer<typeof preComputedJourneyDataSchema>;
export type PreComputedConfidenceScore = z.infer<typeof preComputedConfidenceScoreSchema>;
export type PreComputedTaxSavings = z.infer<typeof preComputedTaxSavingsSchema>;
export type PreComputedTaxSavingsItem = z.infer<typeof preComputedTaxSavingsItemSchema>;
export type PreComputedSSEligibility = z.infer<typeof preComputedSSEligibilitySchema>;
export type PreComputedScenario = z.infer<typeof preComputedScenarioSchema>;
export type PreComputedScenarioComparison = z.infer<typeof preComputedScenarioComparisonSchema>;
export type PreComputedLiquidationItem = z.infer<typeof preComputedLiquidationItemSchema>;
export type PreComputedMilestone = z.infer<typeof preComputedMilestoneSchema>;
export type PreComputedRiskFlag = z.infer<typeof preComputedRiskFlagSchema>;
export type PreComputedHeroStats = z.infer<typeof preComputedHeroStatsSchema>;
export type PreComputedDates = z.infer<typeof preComputedDatesSchema>;
